import type { ReactNode } from "react";
import {
  Box,
  BreadcrumbItem,
  BreadcrumbPage,
  Button,
  DataTableToolbar,
  DataTableToolbarActions,
  Icon,
  Separator,
  Text,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
  type WidgetBoardHistory,
} from "@salestrak/ui";

import {
  DashboardDateRangePill,
  DashboardFilterToolbar,
  type DashboardFilterToolbarProps,
} from "#app/widgets/dashboards/scope/dashboard-filter-toolbar";
import { PageHeader, PageHeaderBreadcrumb } from "#app/components/page-header";
import type { DatePresetValue } from "#app/lib/utils/date-presets";
import type { Omittable } from "@salestrak/ui";

/**
 * The widget host's three header chromes — a dashboard, an overview, and My
 * Work are ONE surface concept (`dashboard.tsx`); the ONLY designed
 * difference between them is which of these headers they wear and which
 * toolbar controls it exposes:
 *
 * - `DashboardMastheadChrome` — managed + titled (My Work): page masthead
 *   with the title breadcrumb; the ownership pill leads the actions cluster
 *   (left of the gear).
 * - `ManagedToolbarChrome` — managed, titleless (Team Overview): compact
 *   toolbar with pill, date range, Manage widgets.
 * - `FreeToolbarChrome` — free dashboards (team/workflow Dashboard tabs):
 *   the full filter toolbar plus the widget controls (undo/redo/Tidy/Start
 *   over/Add widget). The board is always editable for a permitted viewer —
 *   there is no edit mode; unsaved shared edits surface through the
 *   ownership pill's dirty popover.
 *
 * Personalize has no toolbar slot — every viewer's personalization lives in
 * the ownership pill's popover (`ownership-pill.tsx`).
 *
 * All three sit on the page-default surface with a bottom hairline; the board
 * below drops to the recessed sunken surface so widget cards read raised.
 * These are presentation-only: the host owns every piece of state and wires
 * analytics into the callbacks it passes down.
 */

export function DashboardMastheadChrome({
  title,
  pill,
  actions,
}: Omittable<{
  title: string;
  pill: ReactNode;
  actions?: ReactNode;
}>) {
  // The pill leads the actions cluster (left of the gear) rather than
  // trailing the breadcrumb — the ownership state reads as a control, next
  // to the control that edits the surface.
  return (
    <Box background="background">
      <PageHeader
        title={
          <PageHeaderBreadcrumb>
            <BreadcrumbItem>
              <BreadcrumbPage>{title}</BreadcrumbPage>
            </BreadcrumbItem>
          </PageHeaderBreadcrumb>
        }
        actions={
          <Box direction="row" align="center" gap="sm">
            {pill}
            {actions}
          </Box>
        }
      />
      <Separator />
    </Box>
  );
}

export function ManagedToolbarChrome({
  pill,
  dateRange,
  onDateRangeChange,
  canEdit,
  onManageWidgets,
}: {
  pill: ReactNode;
  dateRange: DatePresetValue;
  onDateRangeChange: (next: DatePresetValue) => void;
  canEdit: boolean;
  onManageWidgets: () => void;
}) {
  return (
    <Box background="background">
      <DataTableToolbar>
        <DataTableToolbarActions>
          {pill}
          <DashboardDateRangePill
            value={dateRange}
            onChange={onDateRangeChange}
            shape="pill"
            offVariant="outline"
          />
          {canEdit ? (
            <>
              <Separator orientation="vertical" className="h-5" />
              <Button size="xs" onClick={onManageWidgets}>
                <Text>Manage widgets</Text>
              </Button>
            </>
          ) : null}
        </DataTableToolbarActions>
      </DataTableToolbar>
      <Separator />
    </Box>
  );
}

export function FreeToolbarChrome({
  pill,
  canEdit,
  history,
  onTidy,
  onAddWidget,
  onStartOver,
  ...toolbarProps
}: Omit<DashboardFilterToolbarProps, "leading" | "children"> & {
  pill: ReactNode;
  canEdit: boolean;
  history: Pick<WidgetBoardHistory, "canUndo" | "canRedo" | "undo" | "redo">;
  onTidy: () => void;
  onAddWidget: () => void;
  /** Opens the Start-over dialog (replace the board with a template / blank). */
  onStartOver: () => void;
}) {
  return (
    <Box background="background">
      <DashboardFilterToolbar leading={pill} {...toolbarProps}>
        {!canEdit ? null : (
          <Box direction="row" align="center" gap="xs">
            <Tooltip>
              <TooltipTrigger
                render={
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    aria-label="Undo layout change"
                    disabled={!history.canUndo}
                    onClick={history.undo}
                  />
                }
              >
                <Icon name="undo-2" size="sm" />
              </TooltipTrigger>
              <TooltipPopup side="bottom">
                <Text size="xs">Undo layout change</Text>
              </TooltipPopup>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger
                render={
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    aria-label="Redo layout change"
                    disabled={!history.canRedo}
                    onClick={history.redo}
                  />
                }
              >
                <Icon name="redo-2" size="sm" />
              </TooltipTrigger>
              <TooltipPopup side="bottom">
                <Text size="xs">Redo layout change</Text>
              </TooltipPopup>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger
                render={
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    aria-label="Tidy layout"
                    onClick={onTidy}
                  />
                }
              >
                <Icon name="layout-grid" size="sm" />
              </TooltipTrigger>
              <TooltipPopup side="bottom">
                <Text size="xs">Tidy layout</Text>
              </TooltipPopup>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger
                render={
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    aria-label="Start over from a template"
                    onClick={onStartOver}
                  />
                }
              >
                <Icon name="rotate-ccw" size="sm" />
              </TooltipTrigger>
              <TooltipPopup side="bottom">
                <Text size="xs">Start over from a template</Text>
              </TooltipPopup>
            </Tooltip>
            <Button variant="outline" size="xs" onClick={onAddWidget}>
              <Icon name="plus" size="sm" />
              <Text>Add widget</Text>
            </Button>
          </Box>
        )}
      </DashboardFilterToolbar>
      <Separator />
    </Box>
  );
}
